import { BASE_URL } from '../config/clientConfig';

// ── Token helper ──────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('auth_token');

let refreshPromise = null;

const shouldSkipRefresh = (path) => (
  path.includes('/api/v1/auth/refresh-token') ||
  path.includes('/api/v1/auth/login') ||
  path.includes('/api/v1/auth/register') ||
  path.includes('/api/v1/auth/forgot-password')
);

const parseError = async (res) => {
  const err = await res.json().catch(() => ({}));
  const detail =
    (Array.isArray(err.errors) && err.errors.length > 0)
      ? err.errors.map(e => e.message).join(', ')
      : (err.message || err.error);
  const ex = new Error(detail || `Request failed: ${res.status}`);
  if (err.upgradePrompt) ex.upgradePrompt = true;
  ex.responseData = err;
  ex.status = res.status;
  return ex;
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE_URL}/api/v1/auth/refresh-token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// ── Core request handler ──────────────────────────────────────────────────────
const request = async (method, path, body, { retryOn401 = true, useTokenHeader = true } = {}) => {
  const token = useTokenHeader ? getToken() : null;

  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    const ex = new Error(networkErr.message || 'Network error — please check your connection.');
    ex.status = 0;
    throw ex;
  }

  if (res.status === 401 && retryOn401 && !shouldSkipRefresh(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request(method, path, body, { retryOn401: false, useTokenHeader: false });
    }
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.json();
};

// ── Multipart/FormData request handler ───────────────────────────────────────
const requestForm = async (method, path, formData, { retryOn401 = true, useTokenHeader = true } = {}) => {
  const token = useTokenHeader ? getToken() : null;
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers,
      body: formData,
    });
  } catch (networkErr) {
    const ex = new Error(networkErr.message || 'Network error — please check your connection.');
    ex.status = 0;
    throw ex;
  }

  if (res.status === 401 && retryOn401 && !shouldSkipRefresh(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return requestForm(method, path, formData, { retryOn401: false, useTokenHeader: false });
    }
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.json();
};

// ── Binary download handler (carries the auth header) ─────────────────────────
const requestBlob = async (path, { retryOn401 = true, useTokenHeader = true } = {}) => {
  const token = useTokenHeader ? getToken() : null;
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, { method: 'GET', credentials: 'include', headers });
  } catch (networkErr) {
    const ex = new Error(networkErr.message || 'Network error — please check your connection.');
    ex.status = 0;
    throw ex;
  }

  if (res.status === 401 && retryOn401 && !shouldSkipRefresh(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return requestBlob(path, { retryOn401: false, useTokenHeader: false });
    }
  }

  if (!res.ok) {
    throw await parseError(res);
  }

  return res.blob();
};

// ── Public API client ─────────────────────────────────────────────────────────
// Usage:
//   import { api } from '../utility/apiClient';
//   api.get('/dashboard/kpis')
//   api.post('/auth/login', { email, password })
//   api.postForm('/assets', formData)   ← multipart upload
export const api = {
  get:      (path)           => request('GET',    path),
  post:     (path, body)     => request('POST',   path, body),
  put:      (path, body)     => request('PUT',    path, body),
  patch:    (path, body)     => request('PATCH',  path, body),
  delete:   (path)           => request('DELETE', path),
  postForm: (path, formData) => requestForm('POST', path, formData),
  getBlob:  (path)           => requestBlob(path),
};
